import React, { Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Avatar,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ApiService from '../../services/api-service';
import notifier from '../Notifier';
import { getBalance } from '../../asyncAction/asyncBalance';
import { useDispatch, useSelector } from 'react-redux';
import { getErrorString, getUserDetail, HMS, truncate } from '../../constants/function';
import { Images } from '../../assets/common';
import NoData from '../Nodata';
import { useTheme as useAppTheme } from '../../context/ThemeContext';

// The studio upload form, loaded only when a fan chooses to upload a song.
const UploadMusic = React.lazy(() => import('../../pages/UploadMusic'));

// SIKI <-> USD conversion used across the modal. Canonical $0.07 / SIKI —
// MUST match the backend constant.SONG_REQUEST.USD_PER_SIKI so the displayed
// SIKI/USD matches what the server actually charges.
const USD_PER_SIKI = 0.07;
// Default name-your-price amount when no skip price is configured.
const DEFAULT_CUSTOM_SIKI = 100;

// SIKI high-end brand palette (gold HUD, matching the Subscribe dialog).
const GOLD = '#FFEE20';
const GOLD_BRIGHT = '#F5DC6A';
const GOLD_BORDER = 'rgba(255, 238, 32,0.28)';
const GOLD_BG = 'linear-gradient(135deg, rgba(255, 238, 32,0.14), rgba(255, 238, 32,0.04))';
const HUD_BORDER = 'rgba(255,255,255,0.06)';
const CARD_BG = 'rgba(255,255,255,0.03)';
const PANEL_BG = 'rgba(255,255,255,0.03)';
const DIALOG_BG = 'linear-gradient(180deg, #0C0C12 0%, #08080C 100%)';
const TEXT_PRIMARY = '#E8E8EE';
const MUTED = '#9A9AAB';

// SIKI brand fonts.
const FONT_DISPLAY = "var(--font-display)";
const FONT_BODY = "var(--font-body)";

// Lower rank => closer to the front of the line.
const TIER_RANK = { fol: 0, skip: 1, free: 2, custom: 0 };

const usdToSiki = (usd) => Math.round((Number(usd) || 0) / USD_PER_SIKI);

// "YYYY-MM-DD" release dates are stored as UTC midnight, so show them in UTC.
const formatReleaseDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

/**
 * Why a just-uploaded song can't be requested yet. My songs only lists songs
 * the server will accept (released and not frozen), so a new song missing
 * from it is either scheduled for a later release or not available yet.
 */
const uploadPendingMessage = ({ title, releaseDate }) => {
  const name = title ? `"${title}"` : 'Your song';
  const release = releaseDate ? new Date(releaseDate) : null;
  if (release && !Number.isNaN(release.getTime()) && release.getTime() > Date.now()) {
    return `${name} is uploaded and releases on ${formatReleaseDate(releaseDate)}. You can request it once it's out.`;
  }
  return `${name} is uploaded but isn't available to request yet. It will show under My songs once it is.`;
};
const sikiToUsd = (siki) => (Number(siki) || 0) * USD_PER_SIKI;

export default function SongRequestButton({
  hostId,
  roomId,
  scheduledEventId,
  // A jukebox day's id (from its share link): pins the request to that day.
  slotId,
  isOwner,
  open: controlledOpen,
  onClose: controlledOnClose,
}) {
  const { getCurrentBranding } = useAppTheme();
  const brandName = getCurrentBranding()?.appName || 'SIKI';
  const dispatch = useDispatch();
  const balance = useSelector((state) => state.balance.bal);

  const [internalOpen, setInternalOpen] = useState(false);

  // Host config + live queue.
  const [config, setConfig] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Upcoming scheduled streams the request can target ("Requesting for").
  const [scheduledStreams, setScheduledStreams] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(scheduledEventId || null);

  // Host-defined request sessions the fan can target (ISO date strings). When
  // present these take priority over scheduled streams as the date picker.
  const [selectedSessionDate, setSelectedSessionDate] = useState(null);

  // Song search.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);

  // The signed-in fan's own requestable songs, and the in-dialog upload.
  const [mySongs, setMySongs] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadNotice, setUploadNotice] = useState(null);

  // Tier selection.
  const [selectedTier, setSelectedTier] = useState(null); // 'fol' | 'skip' | 'free' | 'custom'
  const [customSiki, setCustomSiki] = useState(DEFAULT_CUSTOM_SIKI);

  const [submitting, setSubmitting] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const resetState = useCallback(() => {
    setSelectedSong(null);
    setSearchQuery('');
    setSearchResults([]);
    setMySongs([]);
    setUploadOpen(false);
    setUploadNotice(null);
    setSelectedTier(null);
    setCustomSiki(DEFAULT_CUSTOM_SIKI);
    setConfig(null);
    setQueue([]);
    setScheduledStreams([]);
    setSelectedEventId(scheduledEventId || null);
    setSelectedSessionDate(null);
  }, [scheduledEventId]);

  const handleClose = useCallback(() => {
    if (isControlled) {
      if (controlledOnClose) controlledOnClose();
    } else {
      setInternalOpen(false);
    }
    resetState();
  }, [isControlled, controlledOnClose, resetState]);

  // On open: fetch balance + host config + live queue.
  useEffect(() => {
    if (!open) return;
    dispatch(getBalance());
    if (!hostId) return;

    let cancelled = false;
    setLoadingData(true);
    Promise.all([
      ApiService.SONG_REQUEST_HOST_CONFIG(hostId),
      ApiService.SONG_REQUEST_QUEUE(hostId),
      ApiService.SCHEDULE_LIVESTREAMS_USER({ userId: hostId, pageNo: 1, pageSize: 20 }),
    ])
      .then(([configRes, queueRes, scheduleRes]) => {
        if (cancelled) return;
        const cfg = configRes?.success ? configRes.result : null;
        setConfig(cfg);
        setQueue(queueRes?.success && Array.isArray(queueRes.result) ? queueRes.result : []);

        // Upcoming live streams the fan can target. The endpoint returns either
        // { result: [...] } or { response: [...] }; coerce to an array safely.
        const rawEvents = Array.isArray(scheduleRes?.result)
          ? scheduleRes.result
          : Array.isArray(scheduleRes?.response)
          ? scheduleRes.response
          : [];
        const now = Date.now();
        const upcoming = rawEvents
          .filter(
            (ev) =>
              ev?.eventType === 'livestream' &&
              ev?.status === 'created' &&
              new Date(ev.startDate).getTime() > now,
          )
          .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
        setScheduledStreams(upcoming);

        // Default the target: explicit prop, else the soonest upcoming stream, else none.
        setSelectedEventId(scheduledEventId || upcoming[0]?._id || null);

        // Host-defined request sessions take priority as the date picker. Default
        // to the soonest future session when any exist.
        const futureSessions = (Array.isArray(cfg?.requestSessions) ? cfg.requestSessions : [])
          .filter((s) => s?.date && new Date(s.date).getTime() > now)
          .filter((s) => !slotId || s.id === slotId)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setSelectedSessionDate(futureSessions[0]?.date || null);

        // Seed the name-your-price default from the configured skip price.
        if (cfg?.nameYourPrice) {
          const seed = cfg.skipPriceUsd ? usdToSiki(cfg.skipPriceUsd) : DEFAULT_CUSTOM_SIKI;
          setCustomSiki(seed > 0 ? seed : DEFAULT_CUSTOM_SIKI);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        notifier.error(getErrorString(error));
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, hostId, dispatch, scheduledEventId, slotId]);

  // The fan's own songs. The endpoint only returns released, unfrozen songs,
  // the same rule the submit endpoint enforces.
  const loadMySongs = useCallback(async () => {
    const me = getUserDetail()?._id;
    if (!me) return [];
    try {
      const response = await ApiService.MY_MUSIC(`assetId=${me}&type=user`);
      const songs = Array.isArray(response?.result) ? response.result : [];
      setMySongs(songs);
      return songs;
    } catch (error) {
      notifier.error('Failed to load your songs');
      return [];
    }
  }, []);

  useEffect(() => {
    if (open) loadMySongs();
  }, [open, loadMySongs]);

  // Debounced song search (300ms), preserved from the original.
  useEffect(() => {
    if (selectedSong) return undefined;

    const term = searchQuery.trim();
    if (!term) {
      setSearchResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await ApiService.SEARCH(term);
        setSearchResults(response?.result?.music || []);
      } catch (error) {
        notifier.error('Failed to search songs');
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedSong]);

  const handleOpen = () => {
    setInternalOpen(true);
  };

  const handleSongSelect = (song) => {
    setSelectedSong(song);
    setUploadNotice(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // A song uploaded from the dialog is selected straight away when it can be
  // requested; otherwise say why not, rather than allowing a failing submit.
  const handleUploaded = async (uploaded) => {
    setUploadOpen(false);
    const songs = await loadMySongs();
    const ready = songs.find((song) => String(song._id) === String(uploaded?._id));
    if (ready) {
      handleSongSelect(ready);
    } else {
      setUploadNotice(uploadPendingMessage(uploaded || {}));
    }
  };

  const nameYourPrice = !!config?.nameYourPrice;
  const freeEnabled = !!config?.freeEnabled;
  // When the host has paused submissions, fans can still see the queue but
  // cannot select a tier, set an amount, or submit a request.
  const submissionsPaused = !!config?.submissionsPaused;

  // The SIKI amount the active selection costs.
  const requiredSiki = useMemo(() => {
    if (nameYourPrice) {
      return selectedTier === 'free' ? 0 : Math.max(0, Number(customSiki) || 0);
    }
    if (selectedTier === 'fol') return usdToSiki(config?.folPriceUsd);
    if (selectedTier === 'skip') return usdToSiki(config?.skipPriceUsd);
    return 0; // 'free' or nothing selected
  }, [nameYourPrice, selectedTier, customSiki, config]);

  // Where the current selection would land in the queue.
  const landingPosition = useMemo(() => {
    if (!selectedTier) return null;

    if (nameYourPrice) {
      if (selectedTier === 'free') {
        return queue.filter((q) => q.tier === 'free').length + 1;
      }
      const amount = Number(customSiki) || 0;
      return queue.filter((q) => Number(q.tipAmount) >= amount).length + 1;
    }

    const myRank = TIER_RANK[selectedTier];
    return queue.filter((q) => TIER_RANK[q.tier] <= myRank).length + 1;
  }, [selectedTier, nameYourPrice, customSiki, queue]);

  const selectedSession = (config?.requestSessions || []).find(
    (s) => s.date === selectedSessionDate
  );

  const balanceNum = Number(balance) || 0;
  const insufficient = requiredSiki > 0 && balanceNum < requiredSiki;
  const canSubmit =
    !submissionsPaused && !!selectedSong && !!selectedTier && !insufficient && !submitting;

  const handleSubmit = async () => {
    if (!selectedSong) {
      notifier.error('Please select a song');
      return;
    }
    if (!selectedTier) {
      notifier.error('Please choose how to request');
      return;
    }
    if (!hostId) {
      notifier.error('Missing host information. Please refresh and try again.');
      return;
    }
    if (insufficient) {
      notifier.error('Insufficient balance');
      return;
    }

    setSubmitting(true);
    try {
      // When roomId is absent we're submitting a pre-stream request: it stays
      // in escrow with roomId=null until the host goes live, at which point
      // claimPendingRequestsForHost binds it to the new channel.
      const requestData = {
        songId: selectedSong._id,
        hostId,
        tier: selectedTier,
        // In name-your-price mode the server reads tipAmount directly (0 means
        // free), so send it for custom and free; fol/skip are derived server-side.
        ...(selectedTier === 'custom' || (nameYourPrice && selectedTier === 'free')
          ? { tipAmount: requiredSiki }
          : {}),
        ...(roomId ? { roomId } : {}),
        // Target the date the fan picked. A host-defined request session wins:
        // send its ISO streamDate (server validates it against the host's
        // sessions). Otherwise fall back to the scheduled-stream event id, from
        // which the server derives the streamDate.
        ...(selectedSessionDate
          ? {
              streamDate: selectedSessionDate,
              // Tag the request with the day so the server can check it is still open.
              ...(selectedSession?.id ? { slotId: selectedSession.id } : {}),
            }
          : (selectedEventId || scheduledEventId)
          ? { scheduledEventId: selectedEventId || scheduledEventId }
          : {}),
      };

      const response = await ApiService.SONG_REQUEST_SUBMIT(requestData);
      if (response?.success) {
        notifier.success('Song request submitted!');
        handleClose();
        dispatch(getBalance());
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setSubmitting(false);
    }
  };

  // --- shared style fragments ---------------------------------------------

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: TEXT_PRIMARY,
      fontFamily: FONT_BODY,
      borderRadius: '8px',
      backgroundColor: CARD_BG,
      '& fieldset': { borderColor: HUD_BORDER },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
      '&.Mui-focused fieldset': { borderColor: GOLD },
    },
    '& .MuiInputAdornment-root .MuiTypography-root': {
      color: MUTED,
      fontFamily: FONT_BODY,
    },
  };

  // --- render helpers ------------------------------------------------------

  const renderTierCard = (tier, label, priceUsd) => {
    const selected = selectedTier === tier;
    const isFree = tier === 'free';
    const isFol = tier === 'fol';
    const siki = isFree ? 0 : usdToSiki(priceUsd);
    const myRank = TIER_RANK[tier];
    const landing = queue.filter((q) => TIER_RANK[q.tier] <= myRank).length + 1;

    // Front-of-Line gets a richer gold gradient so the premium tier feels worth it.
    const selectedBg = isFol
      ? 'linear-gradient(135deg, rgba(245,220,106,0.22), rgba(255, 238, 32,0.06))'
      : GOLD_BG;
    const restingBg = isFol
      ? 'linear-gradient(135deg, rgba(255, 238, 32,0.06), rgba(255,255,255,0.02))'
      : CARD_BG;

    return (
      <Box
        key={tier}
        onClick={submissionsPaused ? undefined : () => setSelectedTier(tier)}
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          cursor: submissionsPaused ? 'not-allowed' : 'pointer',
          opacity: submissionsPaused ? 0.5 : 1,
          pointerEvents: submissionsPaused ? 'none' : 'auto',
          p: 1.5,
          borderRadius: '12px',
          border: `1px solid ${selected ? GOLD : isFol ? GOLD_BORDER : HUD_BORDER}`,
          background: selected ? selectedBg : restingBg,
          boxShadow: selected ? '0 0 24px rgba(255, 238, 32,0.18)' : 'none',
          transition: 'border-color .15s, background .15s, box-shadow .15s, transform .15s',
          '&:hover': {
            borderColor: GOLD,
            transform: 'translateY(-2px)',
            boxShadow: selected
              ? '0 0 28px rgba(255, 238, 32,0.24)'
              : '0 4px 18px rgba(255, 238, 32,0.12)',
          },
        }}
      >
        {isFol && (
          <Typography
            sx={{
              position: 'absolute',
              top: 8,
              right: 10,
              fontFamily: FONT_BODY,
              fontSize: '8px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: GOLD_BRIGHT,
            }}
          >
            ♔ Premium
          </Typography>
        )}
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: '11px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: selected ? GOLD_BRIGHT : TEXT_PRIMARY,
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: '22px',
            lineHeight: 1.1,
            color: isFree ? TEXT_PRIMARY : GOLD,
          }}
        >
          {isFree ? 'Free' : `$${Number(priceUsd || 0).toFixed(2)}`}
        </Typography>
        {!isFree && (
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', color: MUTED }}>
            {siki} {brandName}
          </Typography>
        )}
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', color: MUTED, mt: 0.5 }}>
          Lands at #{landing}
        </Typography>
      </Box>
    );
  };

  const renderTierSelection = () => {
    // Pricing renders immediately on open so the fan sees the cost before
    // picking a song. Submit stays gated on selectedSong via canSubmit.
    if (nameYourPrice) {
      const customLanding = queue.filter((q) => Number(q.tipAmount) >= (Number(customSiki) || 0)).length + 1;
      return (
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_BODY,
              fontWeight: 600,
              fontSize: '12px',
              color: MUTED,
              mb: 1,
            }}
          >
            Name your price
          </Typography>
          <TextField
            type="number"
            value={customSiki}
            disabled={submissionsPaused}
            onChange={(e) => {
              setSelectedTier('custom');
              setCustomSiki(Math.max(0, parseInt(e.target.value, 10) || 0));
            }}
            onFocus={() => setSelectedTier('custom')}
            fullWidth
            size="small"
            InputProps={{
              endAdornment: <InputAdornment position="end">{brandName}</InputAdornment>,
              inputProps: { min: 0 },
            }}
            sx={fieldSx}
          />
          <Typography
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '13px', color: GOLD, mt: 0.75 }}
          >
            = ${sikiToUsd(customSiki).toFixed(2)} USD
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', color: MUTED, mt: 0.5 }}>
            Higher offers move you up the line. {selectedTier === 'custom' ? `You'd land at #${customLanding}.` : ''}
          </Typography>

          {freeEnabled && (
            <Box sx={{ mt: 1.5 }}>{renderTierCard('free', 'Free', 0)}</Box>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <Typography
          sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '12px', color: MUTED, mb: 1 }}
        >
          Choose your spot
        </Typography>
        <Stack direction="row" spacing={1.5}>
          {renderTierCard('fol', 'Front of Line', config?.folPriceUsd)}
          {renderTierCard('skip', 'Skip the Line', config?.skipPriceUsd)}
          {freeEnabled && renderTierCard('free', 'Free', 0)}
        </Stack>
      </Box>
    );
  };

  const renderStreamDate = () => {
    const dateLabel = (startDate) =>
      new Date(startDate).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });

    // Host-defined request sessions, future-only, soonest first.
    const now = Date.now();
    const futureSessions = (Array.isArray(config?.requestSessions) ? config.requestSessions : [])
      .filter((s) => s?.date && new Date(s.date).getTime() > now)
      .filter((s) => !slotId || s.id === slotId)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return (
      <Box>
        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '12px', color: MUTED, mb: 1 }}>
          Requesting for
        </Typography>

        {futureSessions.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {futureSessions.map((session) => {
                const selected = selectedSessionDate === session.date;
                const label = `${dateLabel(session.date)}${
                  session.label ? ` — ${session.label}` : ''
                }`;
                return (
                  <Box
                    key={session.date}
                    onClick={() => setSelectedSessionDate(session.date)}
                    sx={{
                      cursor: 'pointer',
                      px: 1.5,
                      py: 1,
                      borderRadius: '8px',
                      border: `1px solid ${selected ? GOLD : HUD_BORDER}`,
                      background: selected ? GOLD_BG : PANEL_BG,
                      boxShadow: selected ? '0 0 20px rgba(255, 238, 32,0.16)' : 'none',
                      transition: 'border-color .15s, background .15s, box-shadow .15s',
                      '&:hover': { borderColor: GOLD },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontWeight: 600,
                        fontSize: '12px',
                        color: selected ? GOLD_BRIGHT : TEXT_PRIMARY,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', color: MUTED, mt: 0.75 }}>
              If it's not played within 24h of this date, you're automatically refunded.
            </Typography>
          </>
        ) : scheduledStreams.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {scheduledStreams.map((ev) => {
                const selected = selectedEventId === ev._id;
                return (
                  <Box
                    key={ev._id}
                    onClick={() => setSelectedEventId(ev._id)}
                    sx={{
                      cursor: 'pointer',
                      px: 1.5,
                      py: 1,
                      borderRadius: '8px',
                      border: `1px solid ${selected ? GOLD : HUD_BORDER}`,
                      background: selected ? GOLD_BG : PANEL_BG,
                      boxShadow: selected ? '0 0 20px rgba(255, 238, 32,0.16)' : 'none',
                      transition: 'border-color .15s, background .15s, box-shadow .15s',
                      '&:hover': { borderColor: GOLD },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontWeight: 600,
                        fontSize: '12px',
                        color: selected ? GOLD_BRIGHT : TEXT_PRIMARY,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {dateLabel(ev.startDate)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', color: MUTED, mt: 0.75 }}>
              If it's not played within 24h of this date, you're automatically refunded.
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '13px', color: GOLD }}>
              This artist's next live stream
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', color: MUTED, mt: 0.5 }}>
              Your request waits for their next stream (up to 7 days) and is refunded if it doesn't happen.
            </Typography>
          </>
        )}
      </Box>
    );
  };

  const sectionLabelSx = { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '12px', color: MUTED, mb: 1 };

  const renderSearch = () => {
    return (
      <Box>
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search SIKI for a song..."
          fullWidth
          size="small"
          InputProps={{
            endAdornment: searching ? (
              <InputAdornment position="end">
                <CircularProgress size={18} sx={{ color: GOLD }} />
              </InputAdornment>
            ) : searchQuery ? (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchQuery('')} size="small" sx={{ color: MUTED }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          inputProps={{ 'aria-label': 'Search song' }}
          sx={{ ...fieldSx, mb: searchResults.length || searchQuery ? 1.5 : 0 }}
        />
        <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
          {searchResults.length > 0 ? (
            <Stack spacing={0.5}>
              {searchResults.map((song) => (
                <Box
                  key={song._id}
                  onClick={() => handleSongSelect(song)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <Avatar
                    src={song.thumbnailImg?.filePath || Images.default_music}
                    variant="rounded"
                    sx={{ width: 40, height: 40, borderRadius: '8px' }}
                  />
                  <Box flex={1} minWidth={0}>
                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '14px', color: TEXT_PRIMARY }}>
                      {truncate(song.title, 30)}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '12px', color: MUTED }}>
                      {song.uploader?.firstName} {song.uploader?.lastName} • {HMS(song.duration)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : searchQuery && !searching ? (
            <NoData label="No songs found" />
          ) : null}
        </Box>
      </Box>
    );
  };

  const renderMySongs = () => (
    <TextField
      select
      size="small"
      fullWidth
      value=""
      onChange={(e) => {
        const song = mySongs.find((s) => String(s._id) === e.target.value);
        if (song) handleSongSelect(song);
      }}
      disabled={mySongs.length === 0}
      SelectProps={{ native: true }}
      inputProps={{ 'aria-label': 'My songs' }}
      helperText={mySongs.length === 0 ? 'Songs you have released on SIKI show up here.' : null}
      FormHelperTextProps={{ sx: { color: MUTED, fontFamily: FONT_BODY, mx: 0 } }}
      sx={{ ...fieldSx, '& select option': { background: '#0C0C12', color: TEXT_PRIMARY } }}
    >
      <option value="">{mySongs.length ? 'Choose one of your songs' : 'No released songs yet'}</option>
      {mySongs.map((song) => (
        <option key={song._id} value={String(song._id)}>
          {song.title}
        </option>
      ))}
    </TextField>
  );

  const renderUpload = () => (
    <Box>
      <Button
        onClick={() => setUploadOpen(true)}
        fullWidth
        variant="outlined"
        sx={{
          py: 1.25,
          borderRadius: '10px',
          borderColor: HUD_BORDER,
          backgroundColor: CARD_BG,
          color: TEXT_PRIMARY,
          fontFamily: FONT_BODY,
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': { borderColor: GOLD, backgroundColor: 'rgba(255,255,255,0.05)' },
        }}
      >
        Upload a new song
      </Button>
      {uploadNotice && (
        <Typography role="status" sx={{ fontFamily: FONT_BODY, fontSize: '12px', color: GOLD_BRIGHT, mt: 1 }}>
          {uploadNotice}
        </Typography>
      )}
    </Box>
  );

  // Top to bottom: search the catalog, pick one of your own songs, or upload one.
  const renderSongPicker = () => {
    if (selectedSong) {
      return (
        <Box
          onClick={() => setSelectedSong(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: '12px',
            border: `1px solid ${HUD_BORDER}`,
            backgroundColor: CARD_BG,
            cursor: 'pointer',
            transition: 'border-color .15s, background-color .15s',
            '&:hover': { borderColor: GOLD_BORDER, backgroundColor: 'rgba(255,255,255,0.05)' },
          }}
        >
          <img
            src={selectedSong.thumbnailImg?.filePath || Images.default_music}
            alt={selectedSong.title}
            style={{ width: 46, height: 46, borderRadius: 8, objectFit: 'cover' }}
          />
          <Box flex={1} minWidth={0}>
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '14px', color: TEXT_PRIMARY }}>
              {truncate(selectedSong.title, 30)}
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '12px', color: MUTED }}>
              {selectedSong.uploader?.firstName} {selectedSong.uploader?.lastName}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '12px', color: GOLD }}>
            Change
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        <Box data-testid="picker-search">
          <Typography sx={sectionLabelSx}>Search song</Typography>
          {renderSearch()}
        </Box>
        <Box data-testid="picker-my-songs">
          <Typography sx={sectionLabelSx}>My songs</Typography>
          {renderMySongs()}
        </Box>
        <Box data-testid="picker-upload">
          <Typography sx={sectionLabelSx}>Upload song</Typography>
          {renderUpload()}
        </Box>
      </Stack>
    );
  };

  const renderQueue = () => (
    <Box>
      <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '12px', color: MUTED, mb: 1 }}>
        Live queue
        {landingPosition != null && (
          <Box component="span" sx={{ fontFamily: FONT_BODY, color: GOLD, fontWeight: 700, ml: 1 }}>
            You'd land at #{landingPosition}
          </Box>
        )}
      </Typography>

      <Box
        sx={{
          maxHeight: 220,
          overflow: 'auto',
          border: `1px solid ${HUD_BORDER}`,
          borderRadius: '12px',
          backgroundColor: PANEL_BG,
        }}
      >
        {queue.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '12px', color: MUTED }}>
              {landingPosition != null ? "Queue is empty — you'd be first up!" : 'Queue is empty'}
            </Typography>
          </Box>
        ) : (
          <>
            {queue.map((item, idx) => {
              const insertHere = landingPosition === item.position;
              return (
                <React.Fragment key={`${item.position}-${idx}`}>
                  {insertHere && <QueueInsertMarker position={landingPosition} brandName={brandName} />}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      borderBottom: idx < queue.length - 1 ? `1px solid ${HUD_BORDER}` : 'none',
                    }}
                  >
                    <Typography
                      sx={{
                        width: 24,
                        flexShrink: 0,
                        fontFamily: FONT_BODY,
                        fontWeight: 600,
                        fontSize: '12px',
                        color: MUTED,
                      }}
                    >
                      {item.position}
                    </Typography>
                    <Avatar
                      src={item.thumbnailImg || Images.default_music}
                      variant="rounded"
                      sx={{ width: 32, height: 32, borderRadius: '8px', flexShrink: 0 }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '13px', color: TEXT_PRIMARY }} noWrap>
                        {truncate(item.songTitle || 'Unknown', 28)}
                      </Typography>
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', color: MUTED }} noWrap>
                        {item.artist || ''}
                        {item.requesterName ? ` • ${item.requesterName}` : ''}
                      </Typography>
                    </Box>
                    <TierBadge tier={item.tier} tipAmount={item.tipAmount} brandName={brandName} />
                  </Box>
                </React.Fragment>
              );
            })}
            {/* Selection inserts at the very end of the queue. */}
            {landingPosition != null && landingPosition > queue.length && (
              <QueueInsertMarker position={landingPosition} brandName={brandName} last />
            )}
          </>
        )}
      </Box>
    </Box>
  );

  // --- dialog --------------------------------------------------------------

  const dialog = (
    <Dialog
      open={!!open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--surface, #0C0C12)',
          backgroundImage: 'none',
          color: TEXT_PRIMARY,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.6)',
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <Box>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '17px', fontWeight: 600, color: TEXT_PRIMARY }}>
            Request a song
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '12px', color: MUTED, mt: 0.5 }}>
            Your tip is held until the host accepts, and refunded if they decline.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" title="Close" sx={{ color: MUTED, '&:hover': { color: '#fff' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: '8px !important' }}>
        {loadingData ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress size={28} sx={{ color: GOLD }} />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Paused notice — host has temporarily stopped taking requests. */}
            {submissionsPaused && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  border: `1px solid ${GOLD}`,
                  background: GOLD_BG,
                  boxShadow: '0 0 24px rgba(255, 238, 32,0.12)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_BODY,
                    fontWeight: 700,
                    fontSize: '13px',
                    color: GOLD_BRIGHT,
                  }}
                >
                  This artist has paused song requests right now. Check back soon.
                </Typography>
              </Box>
            )}

            {/* Requesting for — which scheduled stream this targets. */}
            {renderStreamDate()}

            {/* Song selection */}
            <Box>
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '12px', color: MUTED, mb: 1 }}>
                {selectedSong ? 'Your song' : 'Select a song'}
              </Typography>
              {renderSongPicker()}
            </Box>

            {/* Tier / price selection */}
            {renderTierSelection()}

            {/* Live queue */}
            {renderQueue()}

            {/* Balance */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                borderRadius: '12px',
                border: `1px solid ${HUD_BORDER}`,
                backgroundColor: PANEL_BG,
              }}
            >
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '13px', color: MUTED }}>Your balance</Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '15px', fontWeight: 700, color: GOLD }}>
                {balanceNum.toFixed(2)} {brandName}
              </Typography>
            </Box>

            {insufficient && (
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '12px', color: '#ff5a5a', textAlign: 'center' }}>
                Insufficient balance — you need {requiredSiki} {brandName} (${sikiToUsd(requiredSiki).toFixed(2)}). Top up to continue.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: '10px', px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: GOLD,
            borderColor: GOLD_BORDER,
            borderRadius: '10px',
            fontFamily: FONT_BODY,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '13px',
            px: 3,
            '&:hover': { borderColor: GOLD, background: 'rgba(255, 238, 32,0.06)' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            background: 'linear-gradient(135deg, #F5DC6A 0%, #FFEE20 100%)',
            color: '#0A0A0A',
            borderRadius: '10px',
            fontFamily: FONT_BODY,
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 700,
            px: 3,
            boxShadow: 'none',
            transition: 'box-shadow .15s, transform .15s',
            '&:hover': {
              background: 'linear-gradient(135deg, #F8E486 0%, #EFCF4E 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 24px rgba(255, 238, 32,0.28)',
            },
            '&.Mui-disabled': {
              color: 'rgba(255,255,255,0.25)',
              background: 'rgba(255,255,255,0.04)',
            },
          }}
        >
          {submitting
            ? 'Submitting...'
            : requiredSiki > 0
            ? `Request • ${requiredSiki} ${brandName} ($${sikiToUsd(requiredSiki).toFixed(2)})`
            : 'Request'}
        </Button>
      </DialogActions>

      {/* Studio upload form, full screen over the request dialog. */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        fullScreen
        PaperProps={{ sx: { background: DIALOG_BG, color: TEXT_PRIMARY } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setUploadOpen(false)} sx={{ color: MUTED }} aria-label="Close upload">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
          <Suspense fallback={<CircularProgress size={28} sx={{ color: GOLD }} />}>
            {uploadOpen && <UploadMusic onUploaded={handleUploaded} />}
          </Suspense>
        </Box>
      </Dialog>
    </Dialog>
  );

  // Controlled mode: render only the dialog.
  if (isControlled) return dialog;

  // Uncontrolled mode: hosts never see the trigger.
  if (isOwner) return null;
  // Without host/room context there's nothing to request against.
  if (!hostId || !roomId) return null;

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="contained"
        startIcon={
          <img
            src={Images.jukebox}
            alt="Request Song"
            style={{
              width: 18,
              height: 18,
              filter: 'brightness(0) saturate(100%) invert(6%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)',
            }}
          />
        }
        sx={{
          background: 'linear-gradient(135deg, #F5DC6A 0%, #FFEE20 100%)',
          color: '#0A0A0A',
          borderRadius: '10px',
          fontFamily: FONT_BODY,
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 700,
          padding: '8px 16px',
          boxShadow: 'none',
          transition: 'box-shadow .15s, transform .15s',
          '&:hover': {
            background: 'linear-gradient(135deg, #F8E486 0%, #EFCF4E 100%)',
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 24px rgba(255, 238, 32,0.28)',
          },
        }}
      >
        Request Song
      </Button>
      {dialog}
    </>
  );
}

// Small badge showing a queue item's tier (and SIKI amount when present).
function TierBadge({ tier, tipAmount, brandName }) {
  const labels = { fol: 'Front', skip: 'Skip', free: 'Free', custom: 'Offer' };
  const isFree = tier === 'free';
  const amount = Number(tipAmount) || 0;
  return (
    <Box
      sx={{
        flexShrink: 0,
        textAlign: 'right',
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontWeight: 700,
          fontSize: '9px',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: isFree ? MUTED : GOLD,
        }}
      >
        {labels[tier] || tier}
      </Typography>
      {!isFree && amount > 0 && (
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '11px', fontWeight: 700, color: GOLD }}>
          {amount} {brandName}
        </Typography>
      )}
    </Box>
  );
}

// Gold marker showing where the user's pending request would slot in.
function QueueInsertMarker({ position }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.9,
        background: 'linear-gradient(90deg, rgba(245,220,106,0.20), rgba(255, 238, 32,0.08))',
        borderTop: '1px solid rgba(255, 238, 32,0.5)',
        borderBottom: '1px solid rgba(255, 238, 32,0.5)',
        boxShadow: 'inset 0 0 18px rgba(255, 238, 32,0.18)',
      }}
    >
      <Typography
        sx={{
          width: 24,
          flexShrink: 0,
          fontFamily: FONT_BODY,
          fontSize: '12px',
          color: '#F5DC6A',
          fontWeight: 700,
        }}
      >
        {position}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '11px',
          fontWeight: 700,
          color: '#F5DC6A',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          textShadow: '0 0 10px rgba(255, 238, 32,0.35)',
        }}
      >
        Your request lands here
      </Typography>
    </Box>
  );
}
